/** Rich replies (ADR-0088): the chat renderers, the Reply designer, its previews and checks, and the allowed links. */
export default {
  en: {
    chat: {
      group: "Reply options",
      opens: "Opens {{host}}",
      opensHere: "Opens a page in this console",
      does: "Does: {{step}}",
      doesAction: "Does an action",
      used: "Answered",
      send: "Send",
      required: "required",
      choose: "Choose…",
      table: "Table",
      fillThis: "Please fill this in.",
      failed: "That did not go through: {{message}}",
      closed: "This reply can no longer be answered.",
      waiting: "The agent is waiting for your choice.",
      unavailable: "Not available yet",
      continues: "Opens a page, then comes back here",
      opened: "The page opened in a new tab. Come back here when you are done there.",
    },
    designer: {
      title: "Reply with buttons, cards or a form",
      why: "Add choices the person can tap, a list of cards, a table, a form or links. The AI writes its answer; you decide what can be clicked.",
      empty: "This step answers with text only.",
      start: "Design a reply",
      removeAll: "Remove the reply design",
      add: "Add to the reply",
      kind: {
        text: "Text",
        quick_replies: "Quick replies",
        buttons: "Buttons",
        cards: "Card list",
        table: "Table",
        form: "Form",
        link: "Link",
      },
      kindHelp: {
        text: "A line of text with values from earlier steps.",
        quick_replies: "Short answers the person can tap.",
        buttons: "Buttons that send a reply, open a link or do an action.",
        cards: "One card for each item of a list, each with its own buttons.",
        table: "A table with one row for each item of a list.",
        form: "A few questions the person fills in.",
        link: "A link to a page of your organisation.",
      },
      block: "{{kind}} {{n}}",
      moveUp: "Move {{name}} up",
      moveDown: "Move {{name}} down",
      remove: "Remove {{name}}",
      textEn: "Text (English)",
      textAr: "Text (Arabic)",
      labelEn: "Label (English)",
      labelAr: "Label (Arabic)",
      addChoice: "Add a choice",
      addButton: "Add a button",
      removeButton: "Remove the button {{label}}",
      button: "Button {{n}}",
      aiPick: "Let the AI show only the best of these",
      aiPickCount: "How many at most",
      aiPickHelp: "The AI can only choose among these choices. It never makes up new ones.",
      action: {
        title: "When clicked",
        reply: "Send a reply",
        link: "Open a link",
        action: "Do an action",
        handoff: "Continue elsewhere and come back",
        soon: "coming soon",
      },
      handoffUrl: "Page to open",
      handoffUrlHelp: "A payment, a signature or a form on another site. A one-time link back is added, so the page can send the person back. Only addresses your organisation allows are shown.",
      handoffWait: "Wait at this step",
      handoffWaitHelp: "The run waits here until the person comes back from the page (or it gives up).",
      noWaits: "Add a ‘Wait for’ step first.",
      value: "What it sends",
      valueHelp: "Leave empty to send the label. Insert values with {.",
      url: "Web or app address",
      urlHelp: "Insert values with {. Only addresses your organisation allows are shown.",
      step: "Step it runs",
      stepHelp: "When the button is clicked the run continues at this step, with its approvals and rules. An arrow is added to the graph.",
      noSteps: "Add a ‘Use a system’ step first.",
      items: "List to show",
      itemsHelp: "One card (or row) for each item of this list.",
      max: "Show at most",
      cardTitle: "Title",
      subtitle: "Second line",
      image: "Picture address",
      imageHelp: "Optional. A web address of a picture; only allowed websites are shown.",
      cardFields: "More lines",
      addLine: "Add a line",
      lineLabel: "Line name",
      lineValue: "Line value",
      removeLine: "Remove line {{n}}",
      itemValueHelp: "Type { to insert a field of the item.",
      columns: "Columns",
      addColumn: "Add a column",
      column: "Column {{n}}",
      removeColumn: "Remove column {{n}}",
      formTitle: "Form title",
      questions: "Questions",
      addQuestion: "Add a question",
      question: "Question {{n}}",
      removeQuestion: "Remove question {{n}}",
      fieldType: "Answer",
      type: { text: "Text", number: "Number", date: "Date", choice: "One of a list" },
      requiredField: "Required",
      options: "Choices",
      min: "At least",
      maxValue: "At most",
      maxLength: "Longest answer (characters)",
      submit: "Button text",
      linkLabel: "Link text",
    },
    preview: {
      title: "Preview",
      device: "Screen",
      desktop: "Computer",
      phone: "Phone",
      language: "Language",
      channel: "Where",
      website: "Website and console",
      text: "WhatsApp, SMS and e-mail",
      voice: "Voice",
      fromRun: "Shown with the values of the last test run.",
      sample: "Shown with sample values: names in brackets are filled in when the agent runs.",
      empty: "Add something to the reply to see it here.",
    },
    check: {
      title: "Checks",
      dead_button: "The button ‘{{label}}’ leads nowhere: give it a value to send, a web address or a step to run.",
      missing_step: "The button ‘{{label}}’ runs a step that is missing. Choose a ‘Use a system’ step.",
      not_list: "‘{{path}}’ is not a list, so no cards or rows can be made from it.",
      unknown_list: "Choose the list the cards or rows are made from.",
      link_not_allowed: "Links to {{host}} are not allowed for your organisation, so this one would not be shown. An administrator can allow it in Administration › Organisation.",
      missing_wait: "‘{{label}}’ continues elsewhere but has no ‘Wait for’ step to come back to. Choose one.",
      empty_text: "A text has no words yet.",
      none: "No problems found.",
    },
    links: {
      title: "Links in agent replies",
      help: "Agents may show links and app links in their replies only to these places. Links to anywhere else are left out. Links to pages of this console are always allowed. The platform itself never opens them.",
      domains: "Allowed websites",
      domainsHelp: "For example padel.example.com, or *.example.com for all of its sub-sites.",
      schemes: "Allowed apps",
      schemesHelp: "App link names such as myapp (for links that start with myapp://).",
      none: "None: only pages of this console",
      invalidHost: "‘{{value}}’ is not a website name.",
      invalidScheme: "‘{{value}}’ cannot be an app link.",
    },
  },
  ar: {
    chat: {
      group: "خيارات الرد",
      opens: "يفتح {{host}}",
      opensHere: "يفتح صفحة في هذه الواجهة",
      does: "ينفّذ: {{step}}",
      doesAction: "ينفّذ إجراءً",
      used: "تم الرد",
      send: "إرسال",
      required: "مطلوب",
      choose: "اختر…",
      table: "جدول",
      fillThis: "يرجى تعبئة هذا الحقل.",
      failed: "لم يكتمل ذلك: {{message}}",
      closed: "لم يعد بالإمكان الرد على هذا.",
      waiting: "ينتظر الوكيل اختيارك.",
      unavailable: "غير متاح بعد",
      continues: "يفتح صفحة ثم يعود إلى هنا",
      opened: "فُتحت الصفحة في علامة تبويب جديدة. عُد إلى هنا عند الانتهاء منها.",
    },
    designer: {
      title: "الرد بأزرار أو بطاقات أو نموذج",
      why: "أضف خيارات يمكن للشخص النقر عليها، أو قائمة بطاقات، أو جدولاً، أو نموذجاً، أو روابط. يكتب الذكاء الاصطناعي إجابته، وأنت تحدد ما يمكن النقر عليه.",
      empty: "تجيب هذه الخطوة بنص فقط.",
      start: "صمّم رداً",
      removeAll: "إزالة تصميم الرد",
      add: "أضف إلى الرد",
      kind: {
        text: "نص",
        quick_replies: "ردود سريعة",
        buttons: "أزرار",
        cards: "قائمة بطاقات",
        table: "جدول",
        form: "نموذج",
        link: "رابط",
      },
      kindHelp: {
        text: "سطر نصي بقيم من الخطوات السابقة.",
        quick_replies: "إجابات قصيرة يمكن للشخص النقر عليها.",
        buttons: "أزرار ترسل رداً أو تفتح رابطاً أو تنفّذ إجراءً.",
        cards: "بطاقة لكل عنصر في قائمة، ولكل بطاقة أزرارها.",
        table: "جدول بصف لكل عنصر في قائمة.",
        form: "بضعة أسئلة يملؤها الشخص.",
        link: "رابط إلى صفحة لدى مؤسستك.",
      },
      block: "{{kind}} {{n}}",
      moveUp: "تحريك {{name}} إلى الأعلى",
      moveDown: "تحريك {{name}} إلى الأسفل",
      remove: "إزالة {{name}}",
      textEn: "النص (بالإنجليزية)",
      textAr: "النص (بالعربية)",
      labelEn: "العنوان (بالإنجليزية)",
      labelAr: "العنوان (بالعربية)",
      addChoice: "أضف خياراً",
      addButton: "أضف زراً",
      removeButton: "إزالة الزر {{label}}",
      button: "الزر {{n}}",
      aiPick: "دع الذكاء الاصطناعي يعرض أنسب هذه الخيارات فقط",
      aiPickCount: "العدد الأقصى",
      aiPickHelp: "لا يختار الذكاء الاصطناعي إلا من بين هذه الخيارات، ولا يخترع خيارات جديدة أبداً.",
      action: {
        title: "عند النقر",
        reply: "إرسال رد",
        link: "فتح رابط",
        action: "تنفيذ إجراء",
        handoff: "المتابعة في مكان آخر ثم العودة",
        soon: "قريباً",
      },
      handoffUrl: "الصفحة التي تُفتح",
      handoffUrlHelp: "دفع أو توقيع أو نموذج في موقع آخر. يُضاف رابط عودة يُستخدم مرة واحدة كي تعيد الصفحة الشخص. لا تظهر إلا العناوين التي تسمح بها مؤسستك.",
      handoffWait: "الانتظار عند هذه الخطوة",
      handoffWaitHelp: "ينتظر التشغيل هنا حتى يعود الشخص من الصفحة (أو حتى يتوقف عن الانتظار).",
      noWaits: "أضف خطوة ‘انتظار’ أولاً.",
      value: "ما يرسله",
      valueHelp: "اتركه فارغاً لإرسال العنوان. أدرج القيم بكتابة {.",
      url: "عنوان الموقع أو التطبيق",
      urlHelp: "أدرج القيم بكتابة {. لا تظهر إلا العناوين التي تسمح بها مؤسستك.",
      step: "الخطوة التي ينفّذها",
      stepHelp: "عند النقر على الزر يتابع التشغيل من هذه الخطوة، مع موافقاتها وقواعدها. يُضاف سهم إلى المخطط.",
      noSteps: "أضف خطوة ‘استخدام نظام’ أولاً.",
      items: "القائمة المعروضة",
      itemsHelp: "بطاقة (أو صف) لكل عنصر في هذه القائمة.",
      max: "اعرض بحد أقصى",
      cardTitle: "العنوان",
      subtitle: "السطر الثاني",
      image: "عنوان الصورة",
      imageHelp: "اختياري. عنوان صورة على الإنترنت؛ لا تظهر إلا المواقع المسموح بها.",
      cardFields: "أسطر إضافية",
      addLine: "أضف سطراً",
      lineLabel: "اسم السطر",
      lineValue: "قيمة السطر",
      removeLine: "إزالة السطر {{n}}",
      itemValueHelp: "اكتب { لإدراج حقل من العنصر.",
      columns: "الأعمدة",
      addColumn: "أضف عموداً",
      column: "العمود {{n}}",
      removeColumn: "إزالة العمود {{n}}",
      formTitle: "عنوان النموذج",
      questions: "الأسئلة",
      addQuestion: "أضف سؤالاً",
      question: "السؤال {{n}}",
      removeQuestion: "إزالة السؤال {{n}}",
      fieldType: "الإجابة",
      type: { text: "نص", number: "رقم", date: "تاريخ", choice: "واحد من قائمة" },
      requiredField: "مطلوب",
      options: "الخيارات",
      min: "على الأقل",
      maxValue: "على الأكثر",
      maxLength: "أطول إجابة (بالأحرف)",
      submit: "نص الزر",
      linkLabel: "نص الرابط",
    },
    preview: {
      title: "معاينة",
      device: "الشاشة",
      desktop: "حاسوب",
      phone: "هاتف",
      language: "اللغة",
      channel: "أين",
      website: "الموقع والواجهة",
      text: "واتساب والرسائل النصية والبريد",
      voice: "الصوت",
      fromRun: "معروض بقيم آخر تشغيل تجريبي.",
      sample: "معروض بقيم مثال: الأسماء بين القوسين تُملأ عند تشغيل الوكيل.",
      empty: "أضف شيئاً إلى الرد لتراه هنا.",
    },
    check: {
      title: "الفحوصات",
      dead_button: "الزر ‘{{label}}’ لا يؤدي إلى أي مكان: أعطه قيمة يرسلها أو عنوان موقع أو خطوة يشغّلها.",
      missing_step: "الزر ‘{{label}}’ يشغّل خطوة غير موجودة. اختر خطوة ‘استخدام نظام’.",
      not_list: "‘{{path}}’ ليست قائمة، فلا يمكن صنع بطاقات أو صفوف منها.",
      unknown_list: "اختر القائمة التي تُصنع منها البطاقات أو الصفوف.",
      link_not_allowed: "الروابط إلى {{host}} غير مسموح بها لمؤسستك، لذا لن يظهر هذا الرابط. يمكن للمسؤول السماح به من الإدارة › الجهة.",
      missing_wait: "الزر ‘{{label}}’ يتابع في مكان آخر لكن لا توجد خطوة ‘انتظار’ يعود إليها. اختر واحدة.",
      empty_text: "يوجد نص بلا كلمات بعد.",
      none: "لم يُعثر على مشكلات.",
    },
    links: {
      title: "الروابط في ردود الوكلاء",
      help: "لا يمكن للوكلاء عرض روابط مواقع أو تطبيقات في ردودهم إلا إلى هذه الأماكن، وتُحذف الروابط إلى أي مكان آخر. الروابط إلى صفحات هذه الواجهة مسموح بها دائماً. لا تفتح المنصة هذه الروابط بنفسها أبداً.",
      domains: "المواقع المسموح بها",
      domainsHelp: "مثل padel.example.com، أو ‎*.example.com لكل مواقعه الفرعية.",
      schemes: "التطبيقات المسموح بها",
      schemesHelp: "أسماء روابط التطبيقات مثل myapp (للروابط التي تبدأ بـ myapp://).",
      none: "لا شيء: صفحات هذه الواجهة فقط",
      invalidHost: "‘{{value}}’ ليس اسم موقع.",
      invalidScheme: "‘{{value}}’ لا يمكن أن يكون رابط تطبيق.",
    },
  },
};
